import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  Clock,
  Crown,
  Heart,
  Hourglass,
  MapPin,
  MessageCircle,
  ShoppingBag,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  TrendingUp,
  UserRound,
} from 'lucide-react';
import {
  countTotalOrders,
  formatLastAction,
  formatMoney,
  getClienteDisplayName,
  getLastActionDays,
} from '../utils/crmDashboardHelpers';

const ACTION_META = {
  pedido:   { icon: ShoppingBag,   bg: '#F0FDFA', border: 'rgba(13, 148, 136, 0.30)', fg: '#0F766E' },
  reserva:  { icon: Calendar,      bg: '#EEF2FF', border: 'rgba(79, 70, 229, 0.30)',  fg: '#3730A3' },
  visita:   { icon: MapPin,        bg: '#F5F3FF', border: 'rgba(124, 58, 237, 0.30)', fg: '#5B21B6' },
  mensagem: { icon: MessageCircle, bg: '#FFFBEB', border: 'rgba(217, 119, 6, 0.30)',  fg: '#92400E' },
  feedbackPos: { icon: ThumbsUp,   bg: '#F0FDFA', border: 'rgba(13, 148, 136, 0.30)', fg: '#0F766E' },
  feedbackNeg: { icon: ThumbsDown, bg: '#FFF1F2', border: 'rgba(225, 29, 72, 0.30)',  fg: '#9F1239' },
  default:  { icon: Clock,         bg: '#F1F5F9', border: 'rgba(15, 23, 42, 0.12)',   fg: '#475569' },
};

function getActionMeta(action) {
  if (!action) return ACTION_META.default;
  const type = String(action.type || '').toLowerCase();
  if (type === 'feedback') {
    return action.class === 'negativo' ? ACTION_META.feedbackNeg : ACTION_META.feedbackPos;
  }
  return ACTION_META[type] || ACTION_META.default;
}

export function renderMoneyOrDash(value) {
  const n = Number(value || 0);
  if (!n) return <span className="crm-num muted">-</span>;
  return <span className="crm-num"><strong>{formatMoney(n)}</strong></span>;
}

export function renderCountOrDash(value) {
  const n = Number(value || 0);
  if (!n) return <span className="crm-num muted">-</span>;
  return <span className="crm-num"><strong>{n}</strong></span>;
}

export function buildSuggestion(cliente) {
  if (!cliente) return null;
  const status = String(cliente.status || '').toLowerCase();
  const lastDays = getLastActionDays(cliente) ?? 0;
  const hasNegativeFeedback = (cliente.feedbacks || []).some((fb) => fb.class === 'negativo');
  const totalSpent = Number(cliente.totalSpent || 0);
  const totalPedidos = countTotalOrders(cliente);
  const visitas = (cliente.visitas || []).length;
  const reservas = (cliente.reservas || []).length;

  if (status === 'inativo' || lastDays > 60) {
    return {
      label: 'Reativar cliente',
      title: 'Esse cliente sumiu. Vale uma mensagem de retorno.',
      sub: 'Não interage há mais de 60 dias. Uma oferta personalizada pode trazê-lo de volta.',
      icon: Heart,
    };
  }

  if (hasNegativeFeedback) {
    return {
      label: 'Recuperar confiança',
      title: 'Houve um feedback negativo recente.',
      sub: 'Considere uma mensagem reconhecendo o problema e oferecendo algo para reverter.',
      icon: AlertTriangle,
    };
  }

  if (totalSpent >= 500 || totalPedidos >= 20) {
    return {
      label: 'Cuidar do seu VIP',
      title: 'Esse é um dos seus clientes mais valiosos.',
      sub: 'Vale um agradecimento ou um benefício exclusivo para manter a fidelidade.',
      icon: Crown,
    };
  }

  if (visitas >= 3 && totalSpent < 100) {
    return {
      label: 'Estimular consumo',
      title: 'Visita o salão com frequência, mas gasta pouco.',
      sub: 'Sugira itens ou combos para aumentar o ticket médio na próxima visita.',
      icon: TrendingUp,
    };
  }

  if (reservas > 0 && totalPedidos === 0) {
    return {
      label: 'Apresentar delivery',
      title: 'Já reservou no salão, mas nunca pediu delivery.',
      sub: 'Mande o cardápio de delivery e um cupom de boas-vindas para o novo canal.',
      icon: ShoppingBag,
    };
  }

  return {
    label: 'Manter relacionamento',
    title: 'Sem alertas. Cliente em ritmo saudável.',
    sub: 'Continue acompanhando. Um contato pontual pode reforçar o vínculo.',
    icon: Sparkles,
  };
}

export function buildTopInsights(clientes, summary) {
  const arr = Array.isArray(clientes) ? clientes : [];
  const total = arr.length;

  const janelaReativacao = arr.filter((c) => {
    const days = getLastActionDays(c);
    return days !== null && days > 30 && days <= 60;
  }).length;

  const topByGasto = arr.reduce((best, c) => {
    const v = Number(c.totalSpent || 0);
    if (!best || v > Number(best.totalSpent || 0)) return c;
    return best;
  }, null);

  const ativosSemana = arr.filter((c) => {
    const days = getLastActionDays(c);
    return days !== null && days >= 0 && days <= 7;
  }).length;

  const topClienteValido = topByGasto && Number(topByGasto.totalSpent || 0) > 0 ? topByGasto : null;

  return [
    {
      key: 'reativacao',
      label: 'em janela de reativação',
      value: String(janelaReativacao),
      sub: 'Entre 31 e 60 dias inativos · ainda dá tempo de recuperar',
      tone: 'warn',
      icon: Hourglass,
      action: janelaReativacao > 0 ? 'filter' : null,
    },
    {
      key: 'ativo',
      label: ativosSemana === 1 ? 'ativo nesta semana' : 'ativos nesta semana',
      value: String(ativosSemana),
      sub: 'Interagiram nos últimos 7 dias',
      tone: 'good',
      icon: TrendingUp,
      action: ativosSemana > 0 ? 'filter' : null,
    },
    {
      key: 'top',
      label: 'Cliente mais valioso',
      value: topClienteValido ? getClienteDisplayName(topClienteValido) : '-',
      sub: topClienteValido
        ? `${formatMoney(topClienteValido.totalSpent)} em ${countTotalOrders(topClienteValido)} pedidos`
        : `${summary?.total_clientes || total} clientes no total`,
      tone: 'primary',
      icon: Crown,
      cliente: topClienteValido,
      action: topClienteValido ? 'open' : null,
    },
  ];
}

export function SortHeader({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  const arrow = sort.dir === 'asc' ? '↑' : '↓';
  const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined;

  return (
    <div role="columnheader" aria-sort={ariaSort}>
      <button
        type="button"
        className={`crm-sort-button ${active ? 'active' : ''}`}
        onClick={() => onSort(sortKey)}
      >
        <span>{label}</span>
        {active && <span className="crm-sort-arrow" aria-hidden="true">{arrow}</span>}
      </button>
    </div>
  );
}

export function KpiInfo({ text }) {
  return (
    <button type="button" className="crm-info" data-tooltip={text} aria-label={text}>
      <span className="crm-info-icon" aria-hidden="true">i</span>
      <span className="crm-info-label">O que significa?</span>
    </button>
  );
}

export function CustomerAvatar({ photo, initials, size = 'list', health = '' }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showPhoto = Boolean(photo) && !imageFailed;

  useEffect(() => {
    setImageFailed(false);
  }, [photo]);

  return (
    <div className={`crm-customer-avatar ${size} ${health}`} aria-hidden="true">
      {showPhoto ? (
        <img
          src={photo}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setImageFailed(true)}
        />
      ) : initials ? (
        initials
      ) : (
        <UserRound size={size === 'profile' ? 28 : 20} strokeWidth={2} />
      )}
    </div>
  );
}

export function ActionChip({ action }) {
  const meta = getActionMeta(action);
  const Icon = meta.icon;
  return (
    <span
      className="crm-action-chip"
      style={{
        '--action-bg': meta.bg,
        '--action-border': meta.border,
        '--action-fg': meta.fg,
      }}
    >
      <Icon size={11} />
      {formatLastAction(action)}
    </span>
  );
}
